import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Mail, User, X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_GALLERY = [
  { src: "/assets/gallery/g1.jpg", alt: "Zwei Drohnen auf Landepunkten mit mobiler Stromversorgung und Live-Monitor" },
  { src: "/assets/gallery/g5.png", alt: "Einsatz-Setup im Feld mit mehreren Drohnen" },
  { src: "/assets/gallery/g3.webp", alt: "Drei Drohnen startklar auf Landematten" },
  { src: "/assets/gallery/g4.jpg", alt: "DJI Mavic 3E Steuerung und Monitore vor dem Flug" },
  { src: "/assets/gallery/g2.jpg", alt: "Drohne im Flug am blauen Himmel" },
];

const LEISTUNGEN = [
  "Drohneninspektionen",
  "Luftbild- und Luftvideoaufnahmen",
  "Vermessung und Dokumentation",
  "Technische Unterstützung",
  "Baustellendokumentation",
  "Objekt- und Flächenkontrollen",
  "Individuelle Projektlösungen",
  "Digitale Kundendokumentation",
  "Entwicklung moderner Dokumentationsprozesse",
];

const PORTAL_FEATURES = [
  "Aufträge digital anfragen",
  "Anforderungen und Wünsche direkt übermitteln",
  "Projektfortschritte verfolgen",
  "Dokumentationen und Bilder einsehen",
  "Einsatzberichte abrufen",
  "Projektdaten sicher verwalten",
];

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function Team() {
  const [members, setMembers] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [gallery, setGallery] = useState(DEFAULT_GALLERY);

  useEffect(() => {
    document.title = "Das Team — DROPS TFR";
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    axios
      .get(`${BACKEND}/api/team`)
      .then((res) => setMembers(res.data))
      .catch(() => setMembers([]));
    axios
      .get(`${BACKEND}/api/gallery`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setGallery(res.data.map((g) => ({
            src: `${BACKEND}/api/gallery-image/${g.storage_path}`,
            alt: g.caption || "DROPS TFR — Drohneneinsatz",
          })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % gallery.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gallery]);

  return (
    <div className="min-h-screen bg-[#09090B] text-white selection:bg-[#F5B301] selection:text-black">
      <div className="grain" aria-hidden="true" />
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-4 lg:px-16">
          <a href="/" data-testid="team-logo" className="group flex items-center gap-3">
            <img
              src="/assets/logo.png"
              alt="DROPS TFR Wappen"
              className="h-10 w-10 object-contain transition-transform duration-300 group-hover:scale-110"
            />
            <span className="font-display text-lg font-black uppercase tracking-tighter">
              Drops<span className="text-[#F5B301]"> TFR</span>
            </span>
          </a>
          <a
            href="/"
            data-testid="team-back-link"
            className="group flex items-center gap-2 font-tac text-xs tracking-[0.2em] text-white/70 transition-colors duration-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            STARTSEITE
          </a>
        </div>
      </header>

      <main className="pt-36" data-testid="team-page">
        <div className="px-6 pb-16 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">DIE MANNSCHAFT</p>
            <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-7xl">
              Das <span className="text-stroke">Team.</span>
            </h1>
            <p className="mt-7 max-w-xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
              Die Menschen hinter DROPS TFR — mit Funktion und Verantwortung.
              Schnell erreichbar, technisch versiert, im Einsatz zuverlässig.
            </p>
          </motion.div>
        </div>

        {members === null ? (
          <div className="flex justify-center border-t border-white/10 py-32">
            <Loader2 className="h-6 w-6 animate-spin text-[#F5B301]" />
          </div>
        ) : members.length === 0 ? (
          <div className="border-t border-white/10 py-32 text-center font-tac text-xs tracking-[0.25em] text-white/40">
            TEAM WIRD IN KÜRZE VORGESTELLT
          </div>
        ) : (
          <div className="grid border-t border-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {members.map((m, i) => (
              <Reveal
                key={m.id}
                delay={(i % 3) * 0.1}
                className="border-white/10 max-sm:border-b sm:[&:not(:nth-child(2n))]:border-r sm:border-b lg:[&:not(:nth-child(2n))]:border-r-0 lg:[&:not(:nth-child(3n))]:border-r"
              >
                <article data-testid={`team-member-${m.id}`} className="group flex h-full flex-col">
                  <div className="relative aspect-[4/5] overflow-hidden border-b border-white/10 bg-white/[0.03]">
                    {m.image_path ? (
                      <img
                        src={`${BACKEND}/api/team-image/${m.image_path}`}
                        alt={m.name}
                        loading="lazy"
                        className="h-full w-full object-cover brightness-[0.9] contrast-110 grayscale-[45%] transition-all duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <span className="font-display text-6xl font-black tracking-tighter text-white/15 transition-colors duration-500 group-hover:text-[#F5B301]/40">
                          {initials(m.name) || <User className="h-16 w-16 text-white/15" />}
                        </span>
                      </div>
                    )}
                    <span className="absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-[#F5B301] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <span className="absolute bottom-3 right-3 h-4 w-4 border-b-2 border-r-2 border-[#F5B301] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  </div>
                  <div className="flex flex-1 flex-col p-7 lg:p-9">
                    <h2 className="font-display text-2xl font-black uppercase tracking-tighter text-white transition-colors duration-300 group-hover:text-[#F5B301]">
                      {m.name}
                    </h2>
                    <p className="mt-2 font-tac text-[10px] uppercase tracking-[0.3em] text-[#F5B301]">
                      {m.role}
                    </p>
                    {m.text && (
                      <p className="mt-5 font-tac text-xs leading-relaxed text-[#A1A1AA]">
                        {m.text}
                      </p>
                    )}
                    {m.contact && (
                      <a
                        href={m.contact.includes("@") ? `mailto:${m.contact}` : `tel:${m.contact.replace(/\s/g, "")}`}
                        data-testid={`team-contact-${m.id}`}
                        className="group/link mt-auto flex items-center gap-2 pt-6 font-tac text-xs tracking-[0.15em] text-white/70 transition-colors duration-300 hover:text-[#F5B301]"
                      >
                        <Mail className="h-3.5 w-3.5 text-[#F5B301]" />
                        {m.contact}
                      </a>
                    )}
                    {m.source === "staff" && (
                      <a
                        href={`/team/${m.id}`}
                        data-testid={`team-profile-link-${m.id}`}
                        className="mt-auto flex items-center gap-2 pt-6 font-tac text-[11px] tracking-[0.2em] text-[#F5B301] transition-colors duration-300 hover:text-white"
                      >
                        PROFIL & GALERIE ANSEHEN →
                      </a>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        {/* Über uns */}
        <section data-testid="team-about" className="border-t border-white/10 px-6 py-20 lg:px-16">
          <Reveal>
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">ÜBER UNS</p>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl">
              Innovation <span className="text-stroke">aus der Luft.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-14 lg:grid-cols-[1.4fr_1fr]">
            <div className="space-y-10">
              <Reveal>
                <p className="max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
                  <span className="text-white">DROPS Drohnenservice und Technik</span> steht für professionelle
                  Drohnendienstleistungen, moderne Dokumentation und individuelle Lösungen für Unternehmen,
                  Behörden und Privatkunden. Gegründet mit der Idee, Drohnentechnologie sinnvoll und praxisnah
                  einzusetzen, haben wir unser Leistungsangebot kontinuierlich erweitert. Heute bieten wir weit
                  mehr als klassische Luftaufnahmen — wir unterstützen bei Inspektionen, Vermessungen,
                  Dokumentationen, Fotografie sowie technischen Einsätzen und entwickeln unsere Arbeitsabläufe
                  ständig weiter.
                </p>
              </Reveal>
              <Reveal delay={0.05}>
                <h3 className="font-display text-xl font-black uppercase tracking-tighter text-white">Unsere Philosophie</h3>
                <p className="mt-3 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
                  Jeder Auftrag ist einzigartig. Deshalb setzen wir auf individuelle Planung, transparente
                  Kommunikation und eine lückenlose Dokumentation. Unser Ziel ist es, nicht nur hochwertige
                  Drohnenaufnahmen zu liefern, sondern einen echten Mehrwert für die Projekte unserer Kunden zu schaffen.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h3 className="font-display text-xl font-black uppercase tracking-tighter text-white">Ständige Weiterentwicklung</h3>
                <p className="mt-3 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
                  Stillstand gibt es bei uns nicht. Wir investieren regelmäßig in neue Drohnentechnik, moderne
                  Kamerasysteme und innovative Softwarelösungen und passen unsere Dokumentationsprozesse individuell
                  an die Anforderungen unserer Kunden an — immer auf dem neuesten Stand der Technik.
                </p>
              </Reveal>
            </div>
            <div className="space-y-10">
              <Reveal delay={0.08}>
                <div className="border border-white/10 bg-white/[0.02] p-7">
                  <h3 className="font-display text-lg font-black uppercase tracking-tighter text-white">Moderne Dokumentation</h3>
                  <p className="mt-3 font-tac text-xs leading-relaxed text-[#A1A1AA]">
                    Ein besonderer Schwerpunkt liegt auf unserer digitalen Dokumentation. Kunden erhalten auf Wunsch
                    einen persönlichen, geschützten Zugang zu ihrem Projekt:
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {PORTAL_FEATURES.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 font-tac text-xs text-white/70">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F5B301]" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="border border-white/10 bg-white/[0.02] p-7">
                  <h3 className="font-display text-lg font-black uppercase tracking-tighter text-white">Unsere Leistungen</h3>
                  <ul className="mt-5 grid gap-2.5 sm:grid-cols-1">
                    {LEISTUNGEN.map((l) => (
                      <li key={l} className="flex items-start gap-2.5 font-tac text-xs text-white/70">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 bg-[#F5B301]" /> {l}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.05}>
            <div className="mt-14 border-t border-white/10 pt-10">
              <h3 className="font-display text-xl font-black uppercase tracking-tighter text-white">Unser Versprechen</h3>
              <p className="mt-3 max-w-3xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
                Zuverlässigkeit, Qualität und Innovation stehen bei uns an erster Stelle. Wir begleiten unsere Kunden
                von der Planung bis zur fertigen Dokumentation und entwickeln für jede Herausforderung eine passende Lösung.
              </p>
              <p className="mt-6 font-tac text-xs tracking-[0.25em] text-[#F5B301]">
                MODERNE DROHNENDIENSTLEISTUNGEN · DIGITALE DOKUMENTATION · INDIVIDUELLE LÖSUNGEN
              </p>
            </div>
          </Reveal>
        </section>

        {/* Bildergalerie */}
        <section data-testid="team-gallery" className="border-t border-white/10 px-6 py-20 lg:px-16">
          <Reveal>
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">EINBLICKE</p>
            <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl lg:text-5xl">
              Bilder<span className="text-stroke">galerie.</span>
            </h2>
            <p className="mt-6 max-w-xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
              Ausrüstung, Setup und Drohnen im Einsatz — ein Blick hinter die Kulissen von DROPS TFR.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-3">
            {gallery.map((img, i) => (
              <Reveal key={img.src} delay={(i % 3) * 0.08}>
                <button
                  type="button"
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setLightbox(i)}
                  className={`group relative block w-full overflow-hidden border border-white/10 ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className={`w-full object-cover brightness-[0.92] transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-100 ${i === 0 ? "aspect-[4/3] lg:aspect-[16/11]" : "aspect-[4/3]"}`}
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="absolute bottom-3 left-3 right-3 text-left font-tac text-[10px] tracking-[0.2em] text-white opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    {img.alt}
                  </span>
                  <span className="absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-[#F5B301] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      </main>

      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-[#F5B301] hover:text-[#F5B301]"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            data-testid="gallery-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + gallery.length) % gallery.length); }}
            className="absolute left-4 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-[#F5B301] hover:text-[#F5B301] sm:left-8"
            aria-label="Zurück"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <figure className="max-h-[85vh] max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img src={gallery[lightbox].src} alt={gallery[lightbox].alt} className="max-h-[78vh] w-auto object-contain" />
            <figcaption className="mt-4 text-center font-tac text-[11px] tracking-[0.2em] text-white/60">
              {gallery[lightbox].alt} · {lightbox + 1}/{gallery.length}
            </figcaption>
          </figure>
          <button
            data-testid="gallery-lightbox-next"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % gallery.length); }}
            className="absolute right-4 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-[#F5B301] hover:text-[#F5B301] sm:right-8"
            aria-label="Weiter"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}

      <footer className="border-t border-white/10 px-6 py-8 text-center font-tac text-[10px] tracking-[0.25em] text-white/40">
        © 2026 DROPS TFR — SCHNELL. TECHNISCH. ZUVERLÄSSIG.
      </footer>
    </div>
  );
}
